import type {
    PlanCoordinates, ProjektVerortung, VerortungAchse, VerortungGeruest, VerortungGeschoss, VerortungPasspunkt,
    VerortungPlan,
} from '../../types';
import {
    geschossNameNormalisieren, passpunkteUebertragen, utmEingabeLesen,
    type Georeferenz, type Geschoss, type GpsFix, type Utm,
} from '../../services/verortung';

/**
 * Reine Hilfen der Projekt-Einrichtung der Sensor-Verortung
 * (docs/verortung-kamera-vertrag.md, Paket C): Zahlen mit deutschem Komma
 * lesen, Geschosse ändern/importieren/löschen (Plan-Zuordnung mitziehen),
 * Passpunkte und Achsen eines Plans pflegen, Georeferenz per Achsen
 * übertragen. Alles unveränderlich — jede Funktion liefert neue Objekte.
 *
 * Nichts wirft; Eingabefehler kommen als `null` bzw. `{ fehler }` im Klartext.
 */

// ─── Zahlen ──────────────────────────────────────────────────────────────────

/** Vorzeichen (±, +, -, −), Ziffern mit Komma oder Punkt, optional „m“. „3,“ zählt als 3. */
const ZAHL_MUSTER = /^([±+\-−])?\s*(\d+(?:[.,]\d*)?|[.,]\d+)\s*m?$/i;

/** „-3,20“, „−3,2 m“, „+2“, „±0,00“, „3.05“ → Zahl; leer oder unlesbar → null. */
export function zahlLesen(text: string): number | null {
    const roh = (typeof text === 'string' ? text : '').replace(/\s+/g, ' ').trim();
    if (!roh) return null;
    const treffer = ZAHL_MUSTER.exec(roh);
    if (!treffer) return null;
    const betrag = Number(treffer[2].replace(',', '.'));
    if (!Number.isFinite(betrag)) return null;
    const wert = treffer[1] === '-' || treffer[1] === '−' ? -betrag : betrag;
    return wert === 0 ? 0 : wert;
}

/** Zahl im Bereich [min, max] lesen, sonst null. */
export function zahlImBereichLesen(text: string, min: number, max: number): number | null {
    const w = zahlLesen(text);
    return w !== null && w >= min && w <= max ? w : null;
}

/** Ganzzahl im Bereich [min, max] lesen, sonst null. */
export function ganzzahlLesen(text: string, min: number, max: number): number | null {
    const w = zahlImBereichLesen(text, min, max);
    return w !== null && Number.isInteger(w) ? w : null;
}

/** Deutsch mit Komma, ohne Tausender: „3,05“, „-3,20“ — liest `zahlLesen` wieder ein. */
export function zahlText(w: number, stellen: number): string {
    if (!Number.isFinite(w)) return '';
    const text = w.toFixed(stellen);
    // „-0,00“ vermeiden (−0 oder winzige negative Reste).
    return (Number(text) === 0 ? (0).toFixed(stellen) : text).replace('.', ',');
}

/** Anzeige mit Vorzeichen wie im Schnitt: „±0,00“, „+3,05“, „−3,20“. */
export function hoeheText(w: number, stellen: number = 2): string {
    const t = zahlText(Math.abs(w), stellen);
    if (Number(t.replace(',', '.')) === 0) return `±${t}`;
    return `${w > 0 ? '+' : '−'}${t}`;
}

// ─── Geschosse ───────────────────────────────────────────────────────────────

/** Grenzen der Eingabe (Baugrube bis Hochhaus, mit Reserve). */
export const HOEHE_MIN_M = -100;
export const HOEHE_MAX_M = 500;

/** Höhe eines Geschosses bzw. der ersten Gerüstlage lesen. */
export function hoeheLesen(text: string): number | null {
    return zahlImBereichLesen(text, HOEHE_MIN_M, HOEHE_MAX_M);
}

/** Name des Geschosses lesen: getrimmt, einfache Leerzeichen; leer → null (ein Geschoss braucht einen Namen). */
export function geschossNameLesen(text: string): string | null {
    const name = (typeof text === 'string' ? text : '').trim().replace(/\s+/g, ' ');
    return name ? name : null;
}

function nameVergeben(geschosse: readonly VerortungGeschoss[], name: string): boolean {
    const n = geschossNameNormalisieren(name);
    return geschosse.some((g) => geschossNameNormalisieren(g.name) === n);
}

/**
 * Vorschlag für ein neues Geschoss: ohne Geschosse „EG ±0,00“; sonst über dem
 * höchsten (+3,00 m), Name „n. OG“ weitergezählt (EG → 1. OG), sonst „Geschoss n“.
 */
export function neuesGeschoss(geschosse: readonly VerortungGeschoss[], id: string): VerortungGeschoss {
    if (geschosse.length === 0) return { id, name: 'EG', hoeheM: 0 };
    const oberstes = [...geschosse].sort((x, y) => y.hoeheM - x.hoeheM)[0];
    const hoeheM = Math.round((oberstes.hoeheM + 3) * 100) / 100;
    const og = /^(\d+)\.\s*OG$/i.exec(oberstes.name.trim());
    let name = og ? `${Number(og[1]) + 1}. OG` : /^EG$/i.test(oberstes.name.trim()) ? '1. OG' : `Geschoss ${geschosse.length + 1}`;
    for (let i = 2; nameVergeben(geschosse, name); i++) name = `Geschoss ${geschosse.length + i}`;
    return { id, name, hoeheM };
}

/** Ein Geschoss ändern (Name/Höhe); id bleibt. */
export function geschossAendern(
    v: ProjektVerortung,
    id: string,
    patch: Partial<Pick<VerortungGeschoss, 'name' | 'hoeheM'>>,
): ProjektVerortung {
    return { ...v, geschosse: v.geschosse.map((g) => (g.id === id ? { ...g, ...patch } : g)) };
}

/** Wie viele Pläne sind diesem Geschoss zugeordnet? */
export function plaeneMitGeschoss(v: ProjektVerortung, geschossId: string): number {
    return Object.values(v.plaene ?? {}).filter((p) => !!p && p.geschossId === geschossId).length;
}

/** Plan-Zuordnungen auf Geschosse, die es nicht (mehr) gibt, auf null setzen. */
function zuordnungenBereinigen(
    plaene: Record<string, VerortungPlan> | undefined,
    gueltig: ReadonlySet<string>,
): Record<string, VerortungPlan> | undefined {
    if (!plaene) return plaene;
    let geaendert = false;
    const neu: Record<string, VerortungPlan> = {};
    for (const [planId, p] of Object.entries(plaene)) {
        if (p && typeof p.geschossId === 'string' && !gueltig.has(p.geschossId)) {
            neu[planId] = { ...p, geschossId: null };
            geaendert = true;
        } else {
            neu[planId] = p;
        }
    }
    return geaendert ? neu : plaene;
}

/** Geschoss löschen; Pläne, die es nutzten, verlieren die Zuordnung (geschossId null). */
export function geschossLoeschen(v: ProjektVerortung, id: string): ProjektVerortung {
    const geschosse = v.geschosse.filter((g) => g.id !== id);
    const plaene = zuordnungenBereinigen(v.plaene, new Set(geschosse.map((g) => g.id)));
    return { ...v, geschosse, ...(plaene !== undefined ? { plaene } : {}) };
}

/**
 * Freitext-Import (`geschosseLesen`) übernehmen. Gleichnamige Geschosse
 * (getrimmt, case-insensitiv) behalten ihre id — damit bleiben Plan-Zuordnungen
 * erhalten; neue bekommen `neueId()`.
 * - 'ersetzen': die Liste ist genau der Import; Zuordnungen auf weggefallene
 *   Geschosse werden null.
 * - 'ergaenzen': vorhandene bleiben, gleichnamige übernehmen die neue Höhe,
 *   neue kommen dazu.
 * Ergebnis nach Höhe sortiert.
 */
export function geschosseImportieren(
    v: ProjektVerortung,
    importiert: readonly Geschoss[],
    modus: 'ersetzen' | 'ergaenzen',
    neueId: () => string,
): ProjektVerortung {
    const vorhanden = new Map(v.geschosse.map((g) => [geschossNameNormalisieren(g.name), g] as const));
    const nachHoehe = (x: VerortungGeschoss, y: VerortungGeschoss) => x.hoeheM - y.hoeheM;
    if (modus === 'ersetzen') {
        const geschosse = importiert
            .map((g) => ({ id: vorhanden.get(geschossNameNormalisieren(g.name))?.id ?? neueId(), name: g.name, hoeheM: g.hoeheM }))
            .sort(nachHoehe);
        const plaene = zuordnungenBereinigen(v.plaene, new Set(geschosse.map((g) => g.id)));
        return { ...v, geschosse, ...(plaene !== undefined ? { plaene } : {}) };
    }
    const neu = new Map(importiert.map((g) => [geschossNameNormalisieren(g.name), g] as const));
    const geschosse: VerortungGeschoss[] = v.geschosse.map((g) => {
        const treffer = neu.get(geschossNameNormalisieren(g.name));
        return treffer ? { ...g, hoeheM: treffer.hoeheM } : g;
    });
    for (const g of importiert) {
        if (!vorhanden.has(geschossNameNormalisieren(g.name))) geschosse.push({ id: neueId(), name: g.name, hoeheM: g.hoeheM });
    }
    return { ...v, geschosse: geschosse.sort(nachHoehe) };
}

// ─── Gerüst ──────────────────────────────────────────────────────────────────

/** Vorgabe beim Einschalten: erste Lage +2,00 m, Abstand 2,0 m (Vertrag), 10 Lagen. */
export const STANDARD_GERUEST: VerortungGeruest = { ersteLageM: 2, abstandM: 2, lagen: 10 };
export const GERUEST_ABSTAND_MIN_M = 0.5;
export const GERUEST_ABSTAND_MAX_M = 5;
export const GERUEST_LAGEN_MIN = 1;
export const GERUEST_LAGEN_MAX = 40;

/**
 * Kurze Vorschau der Lagen: „Gerüst 1. Lage +2,00 m · Gerüst 2. Lage +4,00 m ·
 * … · Gerüst 10. Lage +20,00 m“ — bei mehr als `max` Lagen die ersten
 * `max − 1` und die letzte. Leer ohne Lagen.
 */
export function lagenVorschau(lagen: readonly { name: string; hoeheM: number }[], max: number = 4): string {
    const text = (l: { name: string; hoeheM: number }) => `${l.name} ${hoeheText(l.hoeheM)} m`;
    if (lagen.length <= max) return lagen.map(text).join(' · ');
    return [...lagen.slice(0, Math.max(1, max - 1)).map(text), '…', text(lagen[lagen.length - 1])].join(' · ');
}

// ─── Pläne ───────────────────────────────────────────────────────────────────

/** Kalibrierung eines Plans ersetzen (unveränderlich). */
export function planSetzen(v: ProjektVerortung, planId: string, plan: VerortungPlan): ProjektVerortung {
    return { ...v, plaene: { ...(v.plaene ?? {}), [planId]: plan } };
}

/** Art setzen; Lageplan und „keine Art“ haben kein Geschoss. */
export function planArtSetzen(plan: VerortungPlan | undefined, art: 'geschoss' | 'lageplan' | null): VerortungPlan {
    const basis: VerortungPlan = { ...(plan ?? {}) };
    if (art === null) delete basis.art;
    else basis.art = art;
    if (art !== 'geschoss') basis.geschossId = null;
    return basis;
}

/** Ist das Ergebnis von `planGeoreferenz` eine gültige Georeferenz? */
export function istGeoreferenz(r: Georeferenz | { fehler: string } | null | undefined): r is Georeferenz {
    return !!r && !('fehler' in r);
}

/** Anzeige der Georeferenz: Maßstab (1 Nachkommastelle), Drehung, Restfehler (ab 3 Punkten). */
export function georeferenzAnzeige(geo: Georeferenz): { massstab: string; drehung: string; restfehler: string | null } {
    return {
        massstab: `${zahlText(geo.meterProPlanbreite, 1)} m je Planbreite`,
        drehung: `${zahlText(geo.drehungGrad, 1)}°`,
        restfehler: geo.restfehlerM === null ? null : `${zahlText(geo.restfehlerM, 1)} m`,
    };
}

function zoneGueltig(z: unknown): z is number {
    return typeof z === 'number' && Number.isInteger(z) && z >= 1 && z <= 60;
}

/** Zone, die für weitere Passpunkte gilt: die des Plans, sobald er Passpunkte hat; sonst null (frei). */
export function zoneFuerPlan(plan: VerortungPlan | undefined): number | null {
    return plan && (plan.passpunkte?.length ?? 0) > 0 && zoneGueltig(plan.zone) ? plan.zone : null;
}

/** Fehlertext bei abweichender Zone. */
export const ZONEN_FEHLER = 'andere UTM-Zone als die übrigen Passpunkte';

/**
 * UTM-Eingabe für einen neuen Passpunkt lesen (`utmEingabeLesen`, Standardzone
 * = Zone des Plans, sonst 32). Hat der Plan schon Passpunkte, muss die Zone passen.
 */
export function utmFuerPlanLesen(text: string, plan: VerortungPlan | undefined): Utm | { fehler: string } {
    const zone = zoneFuerPlan(plan);
    const u = utmEingabeLesen(text, zone ?? 32);
    if ('fehler' in u) return u;
    if (zone !== null && u.zone !== zone) {
        return { fehler: `Zone ${u.zone}: ${ZONEN_FEHLER} (Zone ${zone}).` };
    }
    return u;
}

/** „Passpunkt n“ — erste freie Nummer. */
export function standardPasspunktName(plan: VerortungPlan | undefined): string {
    const namen = new Set((plan?.passpunkte ?? []).map((p) => p.name.trim().toLowerCase()));
    let n = (plan?.passpunkte?.length ?? 0) + 1;
    while (namen.has(`passpunkt ${n}`)) n++;
    return `Passpunkt ${n}`;
}

/** Passpunkt anhängen; die Zone gilt dann für den Plan. */
export function passpunktHinzufuegen(plan: VerortungPlan | undefined, punkt: VerortungPasspunkt, zone: number): VerortungPlan {
    return { ...(plan ?? {}), zone, passpunkte: [...(plan?.passpunkte ?? []), punkt] };
}

export function passpunktLoeschen(plan: VerortungPlan | undefined, id: string): VerortungPlan {
    return { ...(plan ?? {}), passpunkte: (plan?.passpunkte ?? []).filter((p) => p.id !== id) };
}

function achsSchluessel(name: string): string {
    return geschossNameNormalisieren(name);
}

/**
 * Achse setzen; eine gleichnamige (getrimmt, case-insensitiv) wird an die neue
 * Stelle verschoben — samt dem von ihr übertragenen Passpunkt (`quelle: 'achse'`,
 * gleicher Name): dessen UTM stammt vom Quellplan, die Planstelle ist die der Achse.
 */
export function achseSetzen(plan: VerortungPlan | undefined, achse: VerortungAchse): VerortungPlan {
    const s = achsSchluessel(achse.name);
    const bisher = plan?.achsen ?? [];
    const i = bisher.findIndex((a) => achsSchluessel(a.name) === s);
    const stelle = { x: achse.plan.x, y: achse.plan.y };
    const neu = { name: achse.name.trim().replace(/\s+/g, ' '), plan: stelle };
    const achsen = i >= 0 ? bisher.map((a, j) => (j === i ? neu : a)) : [...bisher, neu];
    const ergebnis: VerortungPlan = { ...(plan ?? {}), achsen };
    if (plan?.passpunkte?.some((p) => p.quelle === 'achse' && achsSchluessel(p.name) === s)) {
        ergebnis.passpunkte = plan.passpunkte.map((p) =>
            (p.quelle === 'achse' && achsSchluessel(p.name) === s ? { ...p, plan: { ...stelle } } : p));
    }
    return ergebnis;
}

/** Achse löschen — und den von ihr übertragenen Passpunkt (`quelle: 'achse'`, gleicher Name). */
export function achseLoeschen(plan: VerortungPlan | undefined, name: string): VerortungPlan {
    const s = achsSchluessel(name);
    const ergebnis: VerortungPlan = { ...(plan ?? {}), achsen: (plan?.achsen ?? []).filter((a) => achsSchluessel(a.name) !== s) };
    if (plan?.passpunkte) {
        ergebnis.passpunkte = plan.passpunkte.filter((p) => !(p.quelle === 'achse' && achsSchluessel(p.name) === s));
    }
    return ergebnis;
}

/** Übertragene Passpunkte ersetzen die bisherigen mit `quelle: 'achse'`; GPS/UTM-Punkte bleiben (vorn). */
export function achsPasspunkteZusammenfuehren(
    bisher: readonly VerortungPasspunkt[],
    uebertragen: readonly VerortungPasspunkt[],
): VerortungPasspunkt[] {
    return [...bisher.filter((p) => p.quelle !== 'achse'), ...uebertragen];
}

/**
 * Georeferenz von einem anderen Plan über gleichnamige Achsen übernehmen
 * (`passpunkteUebertragen`). Mindestens 2 gemeinsame Achsen; die Zone der
 * Quelle gilt — hat der Zielplan eigene GPS/UTM-Passpunkte in einer anderen
 * Zone, ist das ein Fehler.
 */
export function achsenUebertragen(
    ziel: VerortungPlan | undefined,
    quelle: VerortungPlan,
    quellGeo: Georeferenz,
    quellName: string,
): { plan: VerortungPlan; anzahl: number } | { fehler: string } {
    const punkte = passpunkteUebertragen(quellGeo, quelle.achsen ?? [], ziel?.achsen ?? []);
    if (punkte.length < 2) {
        const n = punkte.length;
        return {
            fehler: `${n === 0 ? 'Keine' : 'Nur eine'} gemeinsame Achse mit „${quellName}“ — mindestens 2 gleichnamige Achsen auf beiden Plänen nötig (z. B. „A/1“ und „F/9“).`,
        };
    }
    const eigene = (ziel?.passpunkte ?? []).filter((p) => p.quelle !== 'achse');
    if (eigene.length > 0 && zoneGueltig(ziel?.zone) && ziel?.zone !== quellGeo.zone) {
        return { fehler: `„${quellName}“ liegt in Zone ${quellGeo.zone}: ${ZONEN_FEHLER} (Zone ${ziel?.zone}).` };
    }
    const uebertragen: VerortungPasspunkt[] = punkte.map((p) => ({
        id: p.id,
        name: p.name,
        plan: { x: p.plan.x, y: p.plan.y },
        ost: p.ost,
        nord: p.nord,
        quelle: 'achse',
        genauigkeitM: p.genauigkeitM ?? null,
    }));
    return {
        plan: { ...(ziel ?? {}), zone: quellGeo.zone, passpunkte: achsPasspunkteZusammenfuehren(ziel?.passpunkte ?? [], uebertragen) },
        anzahl: uebertragen.length,
    };
}

// ─── Planbild ────────────────────────────────────────────────────────────────

/** Tipp auf das Planbild → Prozent der Bildbreite/-höhe (wie MobileInspectionWizard.handlePlanClick), auf 0–100 begrenzt. */
export function punktAusKlick(
    rect: { left: number; top: number; width: number; height: number },
    clientX: number,
    clientY: number,
): PlanCoordinates | null {
    if (!(rect.width > 0) || !(rect.height > 0) || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
    const begrenzen = (w: number) => Math.round(Math.min(100, Math.max(0, w)) * 1000) / 1000;
    return { x: begrenzen(((clientX - rect.left) / rect.width) * 100), y: begrenzen(((clientY - rect.top) / rect.height) * 100) };
}

/** Neues Seitenverhältnis (Breite/Höhe), wenn es fehlt oder um mehr als 0,1 % abweicht; sonst null. */
export function seitenverhaeltnisNeu(bisher: number | undefined, breite: number, hoehe: number): number | null {
    if (!(breite > 0) || !(hoehe > 0) || !Number.isFinite(breite) || !Number.isFinite(hoehe)) return null;
    const neu = breite / hoehe;
    if (typeof bisher === 'number' && Number.isFinite(bisher) && bisher > 0 && Math.abs(neu / bisher - 1) <= 1e-3) return null;
    return neu;
}

// ─── GPS ─────────────────────────────────────────────────────────────────────

/** Positionen ab dem Start der Messung (nach Empfangszeit) als GPS-Fixe für `gpsMitteln`. */
export function gpsFixeAb(
    positionen: readonly { lat: number; lng: number; genauigkeitM: number; empfangen: number }[],
    start: number,
): GpsFix[] {
    return positionen
        .filter((p) => p.empfangen >= start)
        .map((p) => ({ lat: p.lat, lng: p.lng, genauigkeitM: p.genauigkeitM, t: p.empfangen }));
}
